import { z } from "zod";

// Agent List
const criticalitySchema = z
  .enum([
    "Critical",
    "High",
    "Medium",
    "Low",
    "critical",
    "high",
    "medium",
    "low",
  ])
  .nullable();

const agentMetadataSchema = z
  .object({
    model: z
      .union([
        z.string(),
        z.object({ id: z.string().optional(), speed: z.string().optional() }),
      ])
      .optional(),
    temperature: z.number().optional(),
    instructionPrompt: z.string().optional(),
  })
  .passthrough();

const agentOwnersSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  type: z.string(),
  source: z.string(),
  rank: z.number(),
});

export const agentSchema = z.object({
  agentId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  displayName: z.string().nullable(),
  url: z.string().nullable(),
  ingestionMethod: z.string().nullable(),
  status: z.string().nullable(),
  environment: z.string().nullable(),
  agentType: z.string().nullable(),
  sourceId: z.string().nullable(),
  platform: z.string().nullable(),
  framework: z.string().nullable(),
  version: z.string().nullable(),
  criticality: criticalitySchema,
  agentMetadata: agentMetadataSchema.nullable(),
  owners: z.array(agentOwnersSchema),
  labels: z.record(z.string(), z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
});

const agentListPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  totalCount: z.number(),
  hasNextPage: z.boolean(),
});

export const agentListResponseSchema = z.object({
  data: z.array(agentSchema),
  pagination: agentListPaginationSchema,
});

export type AgentListResponse = z.infer<typeof agentListResponseSchema>;

// Filter Options
export const filterOptions = z.object({
  value: z.string(),
  label: z.string(),
  count: z.number(),
});

const filterOptionsPaginationSchema = z.object({
  totalCount: z.number(),
  hasNextPage: z.boolean(),
});

export const filterOptionsResponseSchema = z.object({
  target: z.string(),
  options: z.array(filterOptions),
  pagination: filterOptionsPaginationSchema,
});

export type FilterOptionsResponse = z.infer<typeof filterOptionsResponseSchema>;

export const labelsObject = z.object({
  key: z.string(),
  count: z.number(),
});

const labelsPaginationSchema = z.object({
  totalCount: z.number(),
  hasNextPage: z.boolean(),
});

export const labelsResponseSchema = z.object({
  data: z.array(labelsObject),
  pagination: labelsPaginationSchema,
});

export type LabelsResponse = z.infer<typeof labelsResponseSchema>;

// Agent Identites
export const agentIdentityApplicationObject = z.object({
  applicationKey: z.string(),
  applicationName: z.string(),
  imageId: z.string().nullable(),
});

export const agentIdentityObject = z.object({
  id: z.string(),
  name: z.string(),
  username: z.string(),
  imageUrl: z.string().nullable(),
  email: z.string(),
  jobTitle: z.string().nullable(),
  department: z.string().nullable(),
  status: z.string(),
  employeeId: z.string().nullable(),
  employeeType: z.string().nullable(),
  manager: z.string().nullable(),
  secondaryManager: z.string().nullable(),
  type: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  location: z.string().nullable(),
  application: agentIdentityApplicationObject,
});

export const agentIdentitiesResponseSchema = z.object({
  data: z.object({
    identities: z.array(agentIdentityObject),
    nextPageToken: z.string(),
  }),
});

export type AgentIdentitiesResponse = z.infer<
  typeof agentIdentitiesResponseSchema
>;
